/**
 * What the review UI knows about each document-review rule (AIVA-8751): its
 * display name, which check it belongs to, which group the review list shows
 * it under, and whether its findings come from an LLM judgment rather than a
 * deterministic check.
 *
 * One table, so the finding card's label and the grouped review list can
 * never disagree about what a rule is called or where it belongs.
 */
import type { Finding } from "./types";

/** The check a rule belongs to — what the finding card's label names. */
export type RuleCheck = "cross_reference" | "defined_term";

/** The review list's groups, finer than the two checks so a long review
 *  breaks into pieces an operator can clear one at a time. */
export type RuleGroupId = "cross_reference" | "formatting" | "defined_term" | "numbering";

interface RuleInfo {
  readonly name: string;
  readonly check: RuleCheck;
  readonly group: RuleGroupId;
  /** Findings decided by an LLM call (word_docreview_judge) rather than a
   *  deterministic check. Each one needs reading before it is applied, so the
   *  review list never applies them in bulk. */
  readonly judged?: boolean;
}

/** Display order of the groups, and their headings. */
export const GROUPS: ReadonlyArray<{ readonly id: RuleGroupId; readonly label: string }> = [
  { id: "cross_reference", label: "Cross-references" },
  { id: "formatting", label: "Formatting & links" },
  { id: "defined_term", label: "Defined terms" },
  // "Numbering", not "Numbering & order": it holds only numbering_order, and
  // "order" read as if alphabetical_order (a Defined terms rule) lived here.
  { id: "numbering", label: "Numbering" },
];

const CHECK_LABEL: Record<RuleCheck, string> = {
  cross_reference: "Cross-reference",
  defined_term: "Defined term",
};

/** Keyed by AIVA-8087's descriptive rule ids. Insertion order is the order
 *  rules are listed in within their group. */
const RULES: Record<string, RuleInfo> = {
  broken_reference: { name: "Broken reference", check: "cross_reference", group: "cross_reference" },
  bracket_heading_mismatch: { name: "Bracket heading mismatch", check: "cross_reference", group: "cross_reference" },
  exhibit_schedule_mismatch: { name: "Exhibit/Schedule mismatch", check: "cross_reference", group: "cross_reference" },
  topic_mismatch: { name: "Topic mismatch", check: "cross_reference", group: "cross_reference", judged: true },
  ambiguous_reference: { name: "Ambiguous reference", check: "cross_reference", group: "cross_reference" },
  // A /check-defined-terms rule (the first in that skill's full-check list).
  // FindingCard's original label table called it "Cross-reference", which
  // filed it under that group even on a defined-terms check. AIVA-8297 made it
  // an LLM judgment.
  defined_term_section_mismatch: {
    name: "Defined term section mismatch",
    check: "defined_term",
    group: "defined_term",
    judged: true,
  },
  missing_hyperlink: { name: "Missing hyperlink", check: "cross_reference", group: "formatting" },
  hyperlink_target_mismatch: {
    name: "Hyperlink target mismatch",
    check: "cross_reference",
    group: "formatting",
    judged: true,
  },
  inconsistent_formatting: { name: "Inconsistent formatting", check: "cross_reference", group: "formatting" },
  shortened_hyperlink_text: { name: "Shortened hyperlink text", check: "cross_reference", group: "formatting" },
  defined_not_used: { name: "Defined but not used", check: "defined_term", group: "defined_term" },
  used_not_defined: { name: "Used but not defined", check: "defined_term", group: "defined_term", judged: true },
  used_before_defined: { name: "Used before defined", check: "defined_term", group: "defined_term" },
  missing_defined_term: { name: "Missing defined term", check: "defined_term", group: "defined_term" },
  alphabetical_order: { name: "Alphabetical order", check: "defined_term", group: "defined_term" },
  numbering_order: { name: "Numbering order", check: "cross_reference", group: "numbering" },
};

/** Fixes that only ever insert a comment — mirrors the backend's
 *  `COMMENT_ONLY_FIX_HANDLERS` (shared/repository/assisted_automation_findings.py),
 *  plus used_before_defined's, which ships with AIVA-8749. */
const COMMENT_ONLY_FIX_HANDLERS = new Set([
  "word.docreview.fix_ambiguous_reference",
  "word.docreview.fix_broken_reference",
  "word.docreview.fix_defined_not_used",
  "word.docreview.fix_defined_term_section_mismatch",
  "word.docreview.fix_exhibit_schedule_mismatch_flag",
  "word.docreview.fix_hyperlink_target_mismatch_flag",
  "word.docreview.fix_missing_defined_term",
  "word.docreview.fix_numbering_order_flag",
  "word.docreview.fix_shortened_hyperlink_text_flag",
  "word.docreview.fix_topic_mismatch",
  "word.docreview.fix_used_not_defined",
  "word.docreview.fix_used_before_defined",
]);

/** Title Case of an unrecognised snake_case id, so a rule added before this
 *  table catches up still reads as words rather than an identifier. */
function titleCase(ruleId: string): string {
  return ruleId
    .split("_")
    .map((word) => (word ? word[0]!.toUpperCase() + word.slice(1) : word))
    .join(" ");
}

export function ruleName(ruleId: string): string {
  return RULES[ruleId]?.name ?? titleCase(ruleId);
}

/** Unknown rules are listed with the cross-references — the same default the
 *  finding card's label always used. */
export function ruleGroup(ruleId: string): RuleGroupId {
  return RULES[ruleId]?.group ?? "cross_reference";
}

export function isJudgedRule(ruleId: string): boolean {
  return RULES[ruleId]?.judged === true;
}

/** "Broken reference · Cross-reference" — the finding card's label line. */
export function ruleLabel(ruleId: string): string {
  return `${ruleName(ruleId)} · ${CHECK_LABEL[RULES[ruleId]?.check ?? "cross_reference"]}`;
}

/** Position of a rule within its group; unknown rules sort last, by id. */
export function ruleOrder(ruleId: string): number {
  const index = Object.keys(RULES).indexOf(ruleId);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

/** What applying a finding does to the document, so the review list can say
 *  so before the operator clicks: a comment for the lawyer, or a change to
 *  the text itself. */
export function isCommentFix(finding: Finding): boolean {
  return COMMENT_ONLY_FIX_HANDLERS.has(finding.fix_handler);
}

/** The "→ …" half of a finding's one-line summary: what the fix will write.
 *  `kind` lets the list colour a proposed replacement differently from a
 *  description or a comment. */
export function fixSummary(finding: Finding): { readonly kind: "replace" | "note"; readonly text: string } {
  const params = finding.fix_params as { correctFullText?: unknown; targetUnderline?: unknown };
  if (!isCommentFix(finding) && typeof params.correctFullText === "string" && params.correctFullText) {
    return { kind: "replace", text: params.correctFullText };
  }
  if (finding.rule_id === "inconsistent_formatting") {
    return { kind: "note", text: "formatting matched to the other references" };
  }
  if (finding.rule_id === "alphabetical_order") {
    return { kind: "note", text: "moved to its alphabetical position" };
  }
  return { kind: "note", text: finding.problem || finding.comment };
}

// The sentence evaluateAlphabeticalOrder opens every alphabetical_order
// comment with; the sort-order note (AIVA-8606) follows it.
const MOVED_SENTENCE_END = "to its correct alphabetical position.";

/**
 * How an alphabetical_order finding's acronyms were sorted (AIVA-8606) —
 * "Acronyms are sorted by abbreviation.", `"NTL" is sorted by its full form,
 * "…".`, or `"NTL" has no full form in the Definitions section, so it is
 * sorted by abbreviation.` — or "" for any other finding, and for one stored
 * before the note existed.
 *
 * The note only exists on the finding's `comment`, after the "Moved … to its
 * correct alphabetical position." sentence. The old finding card showed the
 * whole comment; the grouped list shows a fixed summary instead, which is how
 * the note went missing (AIVA-8751). A finding with a note is therefore shown
 * the way the old card showed it (problem, then the whole comment), and the
 * copied list adds the note to the bullet.
 */
export function sortOrderNote(finding: Finding): string {
  if (finding.rule_id !== "alphabetical_order") return "";
  const comment = finding.comment ?? "";
  const end = comment.indexOf(MOVED_SENTENCE_END);
  return end === -1 ? "" : comment.slice(end + MOVED_SENTENCE_END.length).trim();
}
